import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Curated seed testimonials shown when the guestbook is empty or sparse.
 * Once real reviews land in the database, they are mixed into the pool and
 * 5 testimonials are randomly chosen on each page load.
 */
const SEED_TESTIMONIALS = [
  {
    id: 'seed-1',
    name: 'Camille L.',
    text: 'Je suis venue ici pour respirer dix minutes avant un examen — je suis restée trois mois. Le scan corporel a remplacé mes calmants.',
    rating: 5,
  },
  {
    id: 'seed-2',
    name: 'Mathieu R.',
    text: 'Les affirmations matinales m\'ont sorti d\'une période très sombre. Entendre la voix qui me parle au lieu de mes pensées négatives, ça a tout changé.',
    rating: 5,
  },
  {
    id: 'seed-3',
    name: 'Sophie K.',
    text: 'J\'ai testé une dizaine d\'apps de méditation. Hypnose clé est la seule où je sens un vrai accompagnement humain, pas un robot. Et tout est gratuit, c\'est rare.',
    rating: 5,
  },
  {
    id: 'seed-4',
    name: 'Jean-Pierre M.',
    text: 'À 62 ans, je découvre enfin une respiration qui m\'apaise vraiment. Les étirements le soir avant de dormir sont devenus mon rituel.',
    rating: 5,
  },
  {
    id: 'seed-5',
    name: 'Anaïs T.',
    text: 'L\'application est belle, douce, et surtout sincère. On sent qu\'il y a quelqu\'un derrière qui croit à ce qu\'il propose. Merci pour ce cadeau.',
    rating: 5,
  },
];

function shuffleAndTake(arr, n) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, n);
}

export default function TestimonialsCarousel({ count = 5, autoplayMs = 6000 }) {
  const [allTestimonials, setAllTestimonials] = useState(SEED_TESTIMONIALS);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Fetch real reviews from the guestbook and merge into the pool
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/reviews`).then((r) => {
      if (cancelled) return;
      const real = (r.data?.reviews || [])
        .filter((rv) => rv.text && rv.text.length > 30 && rv.text.length < 400)
        .map((rv) => ({
          id: `review-${rv.id}`,
          name: rv.name || 'Anonyme',
          text: rv.text,
          rating: rv.rating || 5,
        }));
      // Merge seed + real, dedupe by id
      const merged = [...SEED_TESTIMONIALS, ...real];
      const seen = new Set();
      const unique = merged.filter((t) => (seen.has(t.id) ? false : seen.add(t.id)));
      setAllTestimonials(unique);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Pick `count` testimonials at random — stable for the current page view.
  const chosen = useMemo(
    () => shuffleAndTake(allTestimonials, Math.min(count, allTestimonials.length)),
    [allTestimonials, count]
  );

  // Auto-rotate
  useEffect(() => {
    if (paused || chosen.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % chosen.length);
    }, autoplayMs);
    return () => clearInterval(t);
  }, [paused, chosen.length, autoplayMs]);

  const goPrev = useCallback(
    () => setIdx((i) => (i - 1 + chosen.length) % chosen.length),
    [chosen.length]
  );
  const goNext = useCallback(
    () => setIdx((i) => (i + 1) % chosen.length),
    [chosen.length]
  );

  if (chosen.length === 0) return null;
  const current = chosen[idx % chosen.length];

  return (
    <section
      className="rounded-3xl border border-amber-400/20 bg-gradient-to-br from-slate-900 to-slate-950 p-5 sm:p-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-carousel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Quote className="w-4 h-4 text-amber-400" />
          <h3 className="text-amber-200 text-sm font-semibold">Ils témoignent</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={goPrev}
            className="w-7 h-7 rounded-full bg-white/5 hover:bg-amber-500/20 text-white/60 hover:text-amber-300 flex items-center justify-center transition"
            aria-label="Précédent"
            data-testid="testi-prev"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-white/40 text-[10px] font-mono tabular-nums">
            {idx + 1}/{chosen.length}
          </span>
          <button
            onClick={goNext}
            className="w-7 h-7 rounded-full bg-white/5 hover:bg-amber-500/20 text-white/60 hover:text-amber-300 flex items-center justify-center transition"
            aria-label="Suivant"
            data-testid="testi-next"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div key={current.id} className="testimonial-fade min-h-[110px]">
        {/* Stars */}
        <div className="flex items-center gap-0.5 mb-2" aria-label={`${current.rating} étoiles`}>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={12}
              className={i < (current.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-white/15'}
            />
          ))}
        </div>
        <p
          className="text-white/85 text-sm sm:text-base leading-relaxed italic"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          « {current.text} »
        </p>
        <p className="text-amber-200/80 text-xs mt-3 font-medium">— {current.name}</p>
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-1.5 mt-4">
        {chosen.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/15 hover:bg-white/30'
            }`}
            aria-label={`Témoignage ${i + 1}`}
            data-testid={`testi-dot-${i}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes testiFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .testimonial-fade { animation: testiFade 0.45s ease-out both; }
      `}</style>
    </section>
  );
}
